import type {RuleSeverity} from '@github-ui/code-scanning-shared/types/rule-severity'
import type {SecuritySeverity} from '@github-ui/code-scanning-shared/types/security-severity'
import type {CopilotOrg} from '@github-ui/copilot-common-types'
import type {CommentsPreference} from '@github-ui/diff-view-settings/types'
import type {DiffLineType, LineRange} from '@github-ui/diffs/types'
import type {
  Deletable,
  Minimizable,
  Reactable,
  Updatable,
  OrgBlockable,
  Reportable,
} from '@github-ui/pull-requests/page-data/payloads/comment-interfaces'
import type {CommentAuthorAssociation as GraphQlCommentAuthorAssociation} from '@github-ui/server-persisted-queries/types/IssueViewerPage'

// Used to enter dialog mode
export type EnterDialogModeFunction = (opts?: {shouldFocusFirstMarker?: boolean}, threadId?: string) => void

// TODO: rename this to be AnnotationLevel in a future PR to match ENUM on Rails.
// Plural name for ENUM is an odd convention.
export const DiffAnnotationLevels = {
  Failure: 'FAILURE',
  Notice: 'NOTICE',
  Warning: 'WARNING',
} as const

export type DiffAnnotationLevels = (typeof DiffAnnotationLevels)[keyof typeof DiffAnnotationLevels]

export type AnnotationCheckRun = {
  detailsUrl: string
  name: string
}

export type DiffAnnotation = {
  annotationLevel: DiffAnnotationLevels
  appAvatarUrl: string
  appAvatarAltText: string
  checkRun: AnnotationCheckRun
  checkSuiteName: string | null
  databaseId: number
  endLine: number
  hideUntilLoadedAndInView?: boolean
  id: string
  message: string
  path: string
  pathDigest: string
  startLine: number
  title: string
}

export type DiffSide = 'LEFT' | 'RIGHT'

export type SuggestedChangeLineRange = {
  endLineNumber: number
  endOrientation: DiffSide
  startLineNumber: number
  startOrientation: DiffSide
}

/**
 * Identifies a specific suggestion within a comment.
 * - `suggestionIndex` is the 0-based index for user comments with multiple suggestion blocks
 * - `suggestionIndex` is `undefined` for automated review comments (single suggestion per comment)
 */
export type SuggestionIdentifier = {
  commentDatabaseId: number
  suggestionIndex: number | undefined
}

/**
 * Suggestion with additional metadata for display purposes.
 */
export type SuggestionDetails = SuggestionIdentifier & {
  path: string
  bodyHTML: string
  /** Diff entries from automated review suggestions (e.g., Copilot code review) */
  diffEntries?: DiffEntry[]
}

export type SuggestedChange = {
  authorLogin: string
  commentId: string
  commentDatabaseId: number | null | undefined
  path: string
  suggestion: string[]
  threadId: number | null | undefined
  lineRange: SuggestedChangeLineRange
  suggestionIndex: number
}

export type ApplySuggestedChangesValidationData = {
  lineRange?: SuggestedChangeLineRange
}

export type Thread = {
  commentsData?: Comments
  id: string
  isResolved?: boolean
  /**
   * Reflects the outdated state at the head of the PR.
   * @deprecated Use `originalPositioning.isOutdated`; removed later in this migration stack.
   */
  isOutdated?: boolean
  resolvedBy?: string
  resolutionReason?: string | null | undefined
  /**
   * @deprecated Use `currentPositioning`; removed later in this migration stack.
   */
  positioning?: ThreadPositioning
  /**
   * @deprecated Use `currentPositioning` for the viewed diff or `originalPositioning` for historical contexts.
   * Removed later in this migration stack.
   */
  subject?: ThreadSubject
  viewerCanReply?: boolean
  author?: Author | null | undefined
  commentCount?: number
  // Optional because we try to construct threads from commit comments
  // and commit comments do not have this limit in place
  reviewCommentsLimit?: number
  reviewCommentsLimitExceeded?: boolean

  /** @deprecated Use the appropriate positioning object's `endDiffSide`; removed later in this migration stack. */
  diffSide?: DiffSide
  /** Thread metadata shared by current and original positioning; retained on the unpositioned thread. */
  subjectType?: 'LINE' | 'FILE'
  /** @deprecated Use the appropriate positioning object's `endLine`; removed later in this migration stack. */
  line?: number | null | undefined
  /** @deprecated Use the appropriate positioning object's `startLine`; removed later in this migration stack. */
  startLine?: number | null
  /** @deprecated Use the appropriate positioning object's `startDiffSide`; removed later in this migration stack. */
  startDiffSide?: DiffSide | null
  /**
   * The path to the original diff for navigating to outdated comments.
   * This is used when the thread is outdated to navigate to the diff where
   * the comment was originally made.
   * @deprecated Use `originalPositioning.originalDiffPathUri`; removed later in this migration stack.
   */
  originalDiffPathUri?: string | null
  /**
   * The file path where this thread is located.
   * Used for thread preview display and navigation.
   * @deprecated Use the appropriate positioning object's `path`; removed later in this migration stack.
   */
  path?: string
  /**
   * Whether or not to render the thread in the diff lines, used to
   * ignore threads that are only for the side panel. If this is false,
   * it means the rest of the comment data might be incomplete.
   * @deprecated Use current-diff marker placement for eligibility; removed later in this migration stack.
   */
  shouldRenderInDiffLines?: boolean
  /*
   * Whether or not to show a loading placeholder for this thread until
   * it's in view and fully loaded with comment data, used to avoid CLS
   * issues from loading threads asynchronously as the user scrolls.
   */
  hideUntilLoadedAndInView?: boolean
  /**
   * Position in the diff being viewed, provided by the markers map or new-comment input.
   * Undefined if the thread cannot be positioned in that diff.
   * Populated alongside legacy positioning fields until consumers are migrated.
   *
   * This attachment is transitional: without the layout store, derive positioning near the consumer
   * from the unpositioned thread and the viewed comparison's file-tree markers, not in the thread query cache.
   */
  currentPositioning?: CurrentThreadPositioning
  /**
   * Historical position and preview data from the thread preview positioning endpoint.
   * Undefined until hydrated. Current and original positioning describe distinct contexts
   * and should not be used in place of one another.
   * Eventually derive this near historical consumers from the separately cached endpoint response,
   * rather than copying it onto the cached thread.
   */
  originalPositioning?: OriginalThreadPositioning
}

export type ThreadSubjectType = 'LINE' | 'FILE'

/**
 * A derived view model for a thread's position within the diff being viewed, independent of thread storage.
 * Line attributes are null for file level threads or threads whose lines could not be determined.
 */
export interface CurrentThreadPositioning {
  path: string
  startLine: number | null
  startDiffSide: DiffSide | null
  endLine: number | null
  endDiffSide: DiffSide | null
  /** Whether the thread is outdated within the diff represented by this positioning. */
  isOutdated: boolean
}

/**
 * A thread's original position and preview data. Its `isOutdated` is evaluated at the head of the pull request,
 * independently of the comparison being viewed.
 * Derived from the thread preview positioning response; inheritance shares the coordinate shape, not the data source.
 */
export interface OriginalThreadPositioning extends CurrentThreadPositioning {
  /** Context diff lines used to render thread previews. */
  diffLines: StaticDiffLine[]
  /** The path to the diff where the thread was originally created, used to navigate to outdated threads */
  originalDiffPathUri: string | null
}

/**
 * The subject of the thread
 * This mainly refers to positioning info but can also include the diffLines for context
 * @deprecated Use `CurrentThreadPositioning` or `OriginalThreadPositioning` according to the rendering context.
 * Retained for legacy payload conversion during migration.
 */
export type ThreadSubject = {
  diffLines?: StaticDiffLine[]
  endLine?: number | null
  endDiffSide?: DiffSide
  originalEndLine?: number | null
  originalStartLine?: number | null
  startDiffSide?: DiffSide | null
  startLine?: number | null
  isOutdated?: boolean
}

/**
 * Positioning information for a thread
 * This is sourced from the markers map and can change based on what oids are being viewed.
 * @deprecated Use `CurrentThreadPositioning`. Retained for legacy payload conversion during migration.
 */
export type ThreadPositioning = {
  endLine?: number | null
  endDiffSide?: DiffSide
  /**
   * Whether the thread is outdated (i.e., the line it was created on has changed in the latest diff)
   * When CotD is enabled, this can change based on repositioning outcomes.
   * When CotD is not enabled, this reflects the `outdated` property on the Thread.
   */
  isOutdated: boolean
  path: string
  startDiffSide?: DiffSide | null
  startLine?: number | null
}

export type StaticDiffLine = {
  __id?: string
  left: number | null
  right: number | null
  type: DiffLineType
  html: string
  text: string
}

export const CommentReviewVariantType = {
  Vanilla: 'vanilla',
  CodeScanning: 'code_scanning',
  Copilot: 'copilot',
  Dependabot: 'dependabot',
  CodeQuality: 'code_quality',
  Automated: 'automated',
} as const

export type CommentReviewVariantType = (typeof CommentReviewVariantType)[keyof typeof CommentReviewVariantType]

export type Comments = {
  comments: Comment[]
  __id?: string
}

// this should also extend CommentBase but that might cause typing issues with the existing code that uses this type, so we will keep it separate for now
export interface Comment extends Reactable, Minimizable, Deletable, Updatable, OrgBlockable, Reportable {
  author: Author | null | undefined
  authorAssociation: GraphQlCommentAuthorAssociation
  automatedComment?: AutomatedComment | null
  body: string
  bodyHTML: string
  footnoteTextHTML?: string | null
  bodyVersion?: string
  createdAt: string
  publishedAt: string | null | undefined
  currentDiffResourcePath?: string | null
  id: string
  databaseId: number | null | undefined
  lastUserContentEdit: UserContentEdit | null | undefined
  // reference is the associated PullRequest
  reference: {
    number: number | undefined
    // the commitOid if the comment is associated with a commit
    text?: string | null | undefined
    author:
      | {
          login: string
        }
      | null
      | undefined
  }
  repository: {
    id: string
    isPrivate: boolean
    name: string
    owner: {
      id: string
      login: string
      url: string
    }
  }
  reviewId?: number | null
  reviewVariantType?: CommentReviewVariantType | null
  state: CommentState
  viewerCanUnminimize: boolean
  viewerDidAuthor: boolean
  subjectType?: string | undefined
  stafftoolsUrl?: string | null
  url: string
}

export type Author = {
  avatarUrl: string
  id: string
  login: string
  url: string
  userType?: 'agent' | 'bot' | 'human'
  __typename?: 'Author'
}

export type DiffEntry = {
  path: string
  diffLines: Array<Omit<StaticDiffLine, '__id'>>
}

export type CommentState = (typeof CommentStates)[keyof typeof CommentStates]
export const CommentStates = {
  Pending: 'pending',
  Submitted: 'submitted',
}

export type Suggestion = {
  diffEntries: DiffEntry[]
}

export type SuggestionState = (typeof SuggestionStates)[keyof typeof SuggestionStates]
export const SuggestionStates = {
  NoSuggestion: 'no_suggestion',
  Pending: 'pending',
  Present: 'present',
  Outdated: 'outdated',
  Error: 'error',
  Applied: 'applied',
} as const

export type DelegatedDismissalRequest = {
  requester: string
  reason?: string
  status: DelegatedDismissalRequestStatus
  reviewer?: string
}

/**
 * Backend-provided auth config authorizing generation of a commit message title for a suggestion
 * via the commit-message-generation agent (mirrors `CopilotGeneratePullTitleAuthInfo`). Present
 * only when the backend has determined the current suggestion is eligible (kill switch,
 * entitlement, and endpoint restrictions already applied); absent/null otherwise, in which case
 * callers must keep their existing generic fallback commit message instead of requesting one.
 */
export type CommitMessageGenerationConfig = {
  apiUrl: string
  ssoOrganizations: CopilotOrg[]
}

export type DelegatedDismissalRequestStatus =
  (typeof DelegatedDismissalRequestStatuses)[keyof typeof DelegatedDismissalRequestStatuses]
export const DelegatedDismissalRequestStatuses = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
  Cancelled: 'cancelled',
} as const

export type AutomatedComment = {
  id: string
  isDismissed: boolean
  isFixed: boolean
  showFeedbackButtons: boolean
  feedbackOptions: Array<{label: string; value: string}>
  dismissalReason?: string
  dismissedBy?: string
  delegatedDismissalRequest?: DelegatedDismissalRequest
  dismissable: boolean
  delegatedDismissalEnabled: boolean
  dismissalOptions: Array<{value: string; label: string; description: string}>
  // Optional string URL for code scanning comments linking to `Show paths` dialog
  codePathsUrl?: string | null
  /**
   * Classic islands receive this config per eligible current Copilot (CCR) suggestion. Modern
   * React comments may omit it and use the layout-wide config; explicit `null` always denies
   * generation. See `CommitMessageGenerationConfig` for the shape and eligibility notes.
   */
  commitMessageGenerationConfig?: CommitMessageGenerationConfig | null
  message: string
  securitySeverity: SecuritySeverity
  severity: RuleSeverity
  source: AutomatedCommentSourceType
  suggestion?: Suggestion
  suggestionState: SuggestionState
  suggestionErrorMessage?: string | null
  title?: string
  viewerCanDismiss: boolean
  viewerCanReopen: boolean
  showAiNote: boolean
  showFixWithCopilotButton: boolean
  showAiLabel?: boolean
  aiLabelText?: string | null
  aiLabelAriaLabel?: string | null
  foldLongSuggestion?: boolean
  hideSeverityLabel?: boolean
  aiAuthored?: boolean
  __typename?: 'AutomatedComment'
}

export const AutomatedCommentSource = {
  CodeScanning: 'code_scanning',
  CodeQuality: 'code_quality',
  LicenseCompliance: 'license_compliance',
  Copilot: 'copilot',
  CodeScanningAi: 'code_scanning_ai',
} as const

export type AutomatedCommentSourceType = (typeof AutomatedCommentSource)[keyof typeof AutomatedCommentSource]

export const ResolutionReason = {
  Addressed: 'addressed',
  WontFix: 'wont-fix',
  Invalid: 'invalid',
} as const

export type ResolutionReasonType = (typeof ResolutionReason)[keyof typeof ResolutionReason]

export const RESOLUTION_REASON_LABELS: Record<ResolutionReasonType, string> = {
  [ResolutionReason.Addressed]: 'Addressed',
  [ResolutionReason.WontFix]: "Won't fix",
  [ResolutionReason.Invalid]: 'Incorrect',
}

export const RESOLUTION_REASON_OPTIONS: Array<{
  label: string
  value: ResolutionReasonType
  description: string
  icon: 'check' | 'x' | 'dash'
}> = [
  {
    label: RESOLUTION_REASON_LABELS[ResolutionReason.Addressed],
    value: ResolutionReason.Addressed,
    icon: 'check',
    description: 'This suggestion has been addressed.',
  },
  {
    label: RESOLUTION_REASON_LABELS[ResolutionReason.WontFix],
    value: ResolutionReason.WontFix,
    icon: 'x',
    description: "This suggestion won't be addressed.",
  },
  {
    label: RESOLUTION_REASON_LABELS[ResolutionReason.Invalid],
    value: ResolutionReason.Invalid,
    icon: 'dash',
    description: 'This suggestion is incorrect or unhelpful.',
  },
]

/**
 * Returns true if the thread is from a Copilot Code Review.
 * Checks both the `copilot` variant type and the `automated` variant with
 * a `copilot` source on the first comment.
 */
export function isCopilotCodeReviewThread(thread: Thread): boolean {
  const firstComment = thread.commentsData?.comments[0]
  if (!firstComment) return false

  if (firstComment.reviewVariantType === CommentReviewVariantType.Copilot) return true

  if (
    firstComment.reviewVariantType === CommentReviewVariantType.Automated &&
    firstComment.automatedComment?.source === AutomatedCommentSource.Copilot
  ) {
    return true
  }

  return false
}

export type CommentAuthor = {
  avatarUrl: string
  login: string
  url: string
}

export type UserContentEdit = {
  editor: Partial<Author> | null | undefined
  id?: string
  __typename?: 'UserContentEdit'
}

export type SuggestedChangesConfiguration = {
  showSuggestChangesButton: boolean
  isValidSuggestionRange: boolean
  sourceContentFromDiffLines: string | undefined
  onInsertSuggestedChange: () => void
  shouldInsertSuggestedChange?: boolean
}

export type ConfigureSuggestedChangesImplementation = {
  configureSuggestedChangesFromLineRange: (
    lineRange?: LineRange | undefined,
    shouldInsertSuggestedChange?: boolean,
  ) => SuggestedChangesConfiguration | undefined
  shouldStartNewConversationWithSuggestedChange: boolean | undefined
}

export interface ViewerData {
  avatarUrl: string
  diffViewPreference: string
  lineSpacingPreference: 'compact' | 'relaxed'
  commentsPreference?: CommentsPreference
  login: string
  shouldDisplayComments: boolean
  shouldDisplayEditOptions: boolean
  tabSizePreference: number
  viewerCanComment: boolean
  viewerCanApplySuggestion: boolean
}
